import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Observable, catchError, finalize, from, of, Subscription, tap, timer } from 'rxjs';
import { concatMap, mergeMap, retry } from 'rxjs/operators';
import { StripeService } from 'ngx-stripe';
import {
  cardFeedAnimation,
  fullscreenCardAnimation,
  homeOverscrollAnimation,
  inOutAnimation,
  modelFeedAnimation,
  navbarmodelFeedAnimation
} from 'src/app/shared/providers/animations-list';
import {
  CARD_CONTROL_BTN,
  CARD_PURCHASE_STATUS,
  CARD_TYPE,
  CARDS_MODE,
  Cards_Page_State,
  ICard,
  ICardAction,
  ICardFeedModificator,
  INavpanelAction,
  ModelCardRendered,
  NAVPANEL_ACTION_TYPE,
  SUBSCRIPTION_STATUS
} from 'src/app/models/card';
import { ICardCollection } from 'src/app/models/collection';
import { LOCALSTORAGE_VAR } from 'src/app/models/localstorage';
import {
  ADVANCED_SEARCH_BASELINE_COUNT,
  ADVANCED_SEARCH_GENERATION_CONCURRENCY,
  ADVANCED_SEARCH_MAX_COUNT,
  ADVANCED_SEARCH_SHEET_BATCH_DELAY_MS,
  ADVANCED_SEARCH_SHEET_BATCH_SIZE,
  ADVANCED_SEARCH_SHEET_BATCH_RETRY_COUNT,
  ADVANCED_SEARCH_SHEET_BATCH_RETRY_DELAY_MS,
  AdvancedSearchDraft,
  AdvancedSearchGenerationMode,
  AdvancedSearchMode,
  ADVANCED_SEARCH_MODE_LABELS,
  AdvancedSearchPlannerAsset,
  AdvancedSearchPlannerResponse,
  AdvancedSearchPreviewCard,
  AdvancedSearchUiState,
  GenerateAdvancedSearchAssetResponse,
  GenerateAdvancedSearchAssetSheetResponse,
  getAdvancedSearchGenerationModeLabel,
  normalizeAdvancedSearchGenerationMode,
} from 'src/app/models/advanced-search';
import { AuthpopupComponent } from 'src/app/shared/components/authpopup/authpopup.component';
import { ConfirmationDialogComponent, DialogData } from 'src/app/shared/components/dialog/confirmation-dialog.component';
import { CardsService } from 'src/app/shared/providers/cards.service';
import { FirebaseAuthService } from 'src/app/shared/providers/firebase-auth.service';
import { ReviewCollectionsService } from 'src/app/shared/providers/review-collections.service';
import { UserDataService } from 'src/app/shared/providers/user-data.service';
import { UtilityFunctionsService } from 'src/app/shared/providers/utility-functions.service';
import { SettingsService } from 'src/app/shared/providers/settings.service';
import { POPUP_TEXT } from 'src/app/shared/text/popup-texts';
import { MatDialog } from '@angular/material/dialog';
import { WebModelsCardComponent } from '../web-models-card/web-models-card.component';
import { WebaccountComponent } from '../webaccount/webaccount.component';
import { WebsubscriptionComponent } from '../websubscription/websubscription.component';
import { AdvancedSearchService } from '../advanced-search.service';

@Component({
  selector: 'app-extended-store',
  templateUrl: './extended-store.component.html',
  styleUrls: ['./extended-store.component.scss'],
  animations: [
    fullscreenCardAnimation,
    cardFeedAnimation,
    modelFeedAnimation,
    homeOverscrollAnimation,
    navbarmodelFeedAnimation,
    inOutAnimation
  ]
})
export class ExtendedStoreComponent implements OnInit, OnDestroy {
  private readonly advancedSearchBaselineCount = ADVANCED_SEARCH_BASELINE_COUNT;
  private readonly advancedSearchMaxCount = ADVANCED_SEARCH_MAX_COUNT;
  private readonly advancedSearchGenerationConcurrency = ADVANCED_SEARCH_GENERATION_CONCURRENCY;
  private readonly advancedSearchSheetBatchSize = ADVANCED_SEARCH_SHEET_BATCH_SIZE;
  private readonly advancedSearchSheetBatchDelayMs = ADVANCED_SEARCH_SHEET_BATCH_DELAY_MS;
  private readonly advancedSearchSheetBatchRetryCount = ADVANCED_SEARCH_SHEET_BATCH_RETRY_COUNT;
  private readonly advancedSearchSheetBatchRetryDelayMs = ADVANCED_SEARCH_SHEET_BATCH_RETRY_DELAY_MS;

  @ViewChild('modelCardsFeed', { static: false })
  public modelCardsFeedRef: WebModelsCardComponent;

  public currentAnimationState: string = Cards_Page_State.CARD_FEED;
  public CARDS_MODE = CARDS_MODE;
  public readonly storeFeedModificator: ICardFeedModificator = { isStoreFeed: true };
  public advancedSearchUiState: AdvancedSearchUiState = 'idle';
  public advancedSearchCards: AdvancedSearchPreviewCard[] = [];
  public advancedSearchErrorMessage: string = '';
  public normalizedAdvancedSearchPrompt: string = '';
  public currentAdvancedSearchMode: AdvancedSearchMode = 'scene-kit';

  public isNoDataRecieved: boolean = false;
  public errorData: unknown;
  public collectionDetailsSource: ICardCollection;

  public isModelFeedVisible: boolean = false;
  public modelFeedCards: ModelCardRendered[] = [];
  public modelFeedCardStartPosition: number = 0;

  public isCollectionDetailsShown: boolean = false;
  public currentCard: ICard | null = null;
  public isAiPreviewFeedActive: boolean = false;

  private modelIdChangeSubscription: Subscription;
  private routeModelId: string | null = null;
  private routeAdvancedSearchGenerationMode: AdvancedSearchGenerationMode | null = null;
  private isComponentActive: boolean = true;
  private activeLoadToken: number = 0;
  private advancedSearchRequestToken: number = 0;
  private planningSubscription?: Subscription;
  private generationSubscription?: Subscription;
  private accountPopupSubscription?: Subscription;
  private subscriptionPopupSubscription?: Subscription;

  constructor(
    private router: Router,
    private readonly route: ActivatedRoute,
    private dialog: MatDialog,
    private stripeService: StripeService,
    public firebaseAuth: FirebaseAuthService,
    public cardService: CardsService,
    private advancedSearchService: AdvancedSearchService,
    public reviewCollectionsService: ReviewCollectionsService,
    public userDataService: UserDataService,
    public utilityService: UtilityFunctionsService,
    private readonly settingsService: SettingsService,
  ) {}

  async ngOnInit(): Promise<void> {
    this.modelIdChangeSubscription = this.route.queryParamMap.subscribe((queryParams) => {
      this.routeModelId = queryParams.get('modelId');
      this.routeAdvancedSearchGenerationMode = normalizeAdvancedSearchGenerationMode(queryParams.get('generationMode'));
      this.tryOpenModelFromQueryParam();
    });

    try {
      await this.reloadPublishedReviewsCollection();
    } catch (error) {
      this.utilityService.rollbarError(
        `User ${localStorage.getItem(LOCALSTORAGE_VAR.ORIGINAL_UID)}, Error fetching published reviews: ${error}`
      );
      this.router.navigate(['/']);
      this.dialog.open<ConfirmationDialogComponent, DialogData>(ConfirmationDialogComponent, {
        width: '370px',
        data: this.utilityService.applyPopupText(POPUP_TEXT.SHARED_CONTENT_NOT_AVAILABLE, '{contentType}', 'Collection'),
      });
    }
  }

  public onLibraryClick(): void {
    this.invalidateActiveLoad();
    this.router.navigate(['/library']);
  }

  public onAccountClick(): void {
    if (this.firebaseAuth.currentUser) {
      this.openAccountPopup();
      return;
    }

    this.triggerShowAuthPopup();
  }

  public onComposerSubmit(draft: AdvancedSearchDraft): void {
    if (this.isAdvancedSearchBusy) {
      return;
    }

    const prompt = draft?.prompt?.trim() || '';
    if (!prompt) {
      this.utilityService.showSnackBar('Describe what you want to generate.', 0);
      return;
    }

    this.startAdvancedSearch(prompt);
  }

  public onComposerCancel(): void {
    this.cancelActiveAdvancedSearchGeneration();
    this.resetAdvancedSearchState('idle');
  }

  public get isAdvancedSearchActive(): boolean {
    return this.advancedSearchUiState !== 'idle' || this.advancedSearchCards.length > 0;
  }

  public get isAdvancedSearchBusy(): boolean {
    return this.advancedSearchUiState === 'planning' || this.advancedSearchUiState === 'generating';
  }

  public get hasAdvancedSearchErrors(): boolean {
    return this.advancedSearchCards.some((card) => card.status === 'error');
  }

  public get activeFeedModificator(): ICardFeedModificator {
    if (this.isAiPreviewFeedActive) {
      return { isAiPreviewFeed: true };
    }

    return this.storeFeedModificator;
  }

  public onAiPreviewSelected(cardId: string): void {
    const successfulCards = this.advancedSearchCards.filter((card) => {
      return card.status === 'success' && !!(card.thumbnailImageDataUrl || card.imageDataUrl);
    });
    const startPosition = successfulCards.findIndex((card) => card.id === cardId);
    if (startPosition < 0) {
      return;
    }

    this.isAiPreviewFeedActive = true;
    this.modelFeedCards = successfulCards.map((card, index) => this.createAiViewerCard(card, index));
    this.modelFeedCardStartPosition = startPosition;
    this.cardService.modelFeedInitStart = performance.now();
    this.isModelFeedVisible = true;
  }

  public onAiPreviewRetry(cardId: string): void {
    const cardToRetry = this.advancedSearchCards.find((card) => card.id === cardId);
    if (!cardToRetry) {
      return;
    }

    this.logAdvancedSearchEvent('Retry requested', {
      cardId: cardToRetry.id,
      title: cardToRetry.title,
      generationMode: this.resolveAdvancedSearchGenerationMode(),
      searchMode: this.currentAdvancedSearchMode,
    });

    this.updateAdvancedSearchCard(cardId, {
      status: 'loading',
      errorMessage: undefined,
    });

    this.advancedSearchService.generateAssetPreview({
      assetId: cardToRetry.id,
      title: cardToRetry.title,
      imagePrompt: cardToRetry.imagePrompt,
      normalizedPrompt: this.normalizedAdvancedSearchPrompt,
      searchMode: this.currentAdvancedSearchMode,
      generationMode:
        'single-pro',
    }).subscribe({
      next: (response) => this.applyGeneratedPreview(response),
      error: (error) => {
        const errorMessage = this.handleAdvancedSearchError('retry', error, {
          assets: [cardToRetry],
          prompt: this.normalizedAdvancedSearchPrompt,
        });

        this.updateAdvancedSearchCard(cardId, {
          status: 'error',
          errorMessage,
        });

        this.advancedSearchErrorMessage = errorMessage;
      }
    });
  }

  public onCollectionDetailsThumbClick(thumbNumber: number): void {
    this.isAiPreviewFeedActive = false;
    this.modelFeedCards = this.collectionDetailsSource.collectionCards;
    this.cardService.modelFeedInitStart = performance.now();
    this.modelFeedCardStartPosition = thumbNumber;
    this.isModelFeedVisible = true;
  }

  public onStoreThumbClicked(card: ModelCardRendered): void {
    if (!card || !this.collectionDetailsSource?.collectionCards?.length) {
      return;
    }

    const thumbIndex = this.collectionDetailsSource.collectionCards.findIndex((item) => item.id === card.id);
    if (thumbIndex < 0) {
      return;
    }

    this.updateModelIdQueryParam(card.id);
    this.onCollectionDetailsThumbClick(thumbIndex);
  }

  public onCardVisibilityChanged(card: ModelCardRendered): void {
    return;
  }

  public trackByAdvancedSearchCardId(index: number, card: AdvancedSearchPreviewCard): string {
    return card.id || `advanced-search-card-${index}`;
  }

  public onCardCollectionControlBtnClicked(action: ICardAction): void {
    this.utilityService.triggerHaptics();

    switch (action.controlAction) {
      case CARD_CONTROL_BTN.ANONYMOUS_USER:
        this.triggerShowAuthPopup();
        break;
      case CARD_CONTROL_BTN.CONTROL_ACTIONS_DEFAULT:
      case CARD_CONTROL_BTN.CONTROL_ACTION_FOR_LIKED:
        this.utilityService.notImplemented();
        break;
      case CARD_CONTROL_BTN.UNLIKE_DRAFT_CARD:
        this.modelCardsFeedRef.dispatchCurrentCardLikeClick();
        break;
      case CARD_CONTROL_BTN.CONTROL_GO_TO_DETAILS:
        if (!this.isModelFeedVisible) {
          this.currentCard = action.card;
          this.cardService.triggerMoreBtnSubject();
        }
        break;
      case CARD_CONTROL_BTN.CONTROL_BACK_BTN:
        if (this.isAiPreviewFeedActive) {
          this.isAiPreviewFeedActive = false;
          this.isModelFeedVisible = false;
          return;
        }

        if (!this.currentCard) {
          return;
        }

        this.cardService.setCurrentCard(this.currentCard);
        this.updateModelIdQueryParam(null);
        this.isModelFeedVisible = false;
        break;
      case CARD_CONTROL_BTN.SEARCH:
      case CARD_CONTROL_BTN.BUY_MODEL:
        this.utilityService.showSnackBar('Coming soon', 0);
        break;
      case CARD_CONTROL_BTN.CANCEL_PUBLISH:
        this.openCancelPublishingCardConfirmationDialog(action.card);
        break;
      default:
        break;
    }
  }

  private async reloadPublishedReviewsCollection(options: { restoreFromQueryParam?: boolean } = {}): Promise<void> {
    const loadToken = ++this.activeLoadToken;
    const reviewCollection = await this.reviewCollectionsService.getPublicStoreCollection();
    if (!this.isLoadTokenActive(loadToken)) {
      return;
    }

    this.applyPublishedReviewsCollection(reviewCollection);
    if (options.restoreFromQueryParam !== false) {
      this.tryOpenModelFromQueryParam();
    }
  }

  private applyPublishedReviewsCollection(sourceCollection: ICardCollection): void {
    if (!this.isComponentActive) {
      return;
    }

    this.collectionDetailsSource = this.patchModelsPurchaseStatusToDefault(sourceCollection);
    const collectionCard = this.cardService.convertCollectionsToCardsFeed([this.collectionDetailsSource])[0];
    if (!collectionCard) {
      return;
    }

    this.cardService.setCurrentCard(collectionCard);
    this.applyMoreState(collectionCard);
    this.utilityService.rollbarInfo(`Published reviews collection opened, userUid: ${this.firebaseAuth.currentUser?.uid}`);
  }

  private patchModelsPurchaseStatusToDefault(sourceCollection: ICardCollection): ICardCollection {
    const patchedCards = (sourceCollection?.collectionCards || []).map((card) => ({
      ...card,
      purchaseStatus: CARD_PURCHASE_STATUS.DEFAULT_3DWAY,
    })) as ModelCardRendered[];

    return {
      ...sourceCollection,
      collectionCards: patchedCards,
    };
  }

  private tryOpenModelFromQueryParam(): void {
    if (!this.routeModelId || !this.collectionDetailsSource?.collectionCards?.length) {
      return;
    }

    const targetIndex = this.findCollectionCardIndexByModelIdentifier(this.routeModelId);
    if (targetIndex < 0) {
      return;
    }

    if (this.isModelFeedVisible && this.modelFeedCardStartPosition === targetIndex) {
      return;
    }

    this.onCollectionDetailsThumbClick(targetIndex);
  }

  private findCollectionCardIndexByModelIdentifier(modelIdentifier: string): number {
    const normalizedRequestedModelIdentifier = this.normalizeModelIdentifier(modelIdentifier);
    if (!normalizedRequestedModelIdentifier) {
      return -1;
    }

    return this.collectionDetailsSource.collectionCards.findIndex((card) => {
      const normalizedCardId = this.normalizeModelIdentifier(card.id);
      const normalizedPublicModelId = this.normalizeModelIdentifier(card.publicModelId);

      return (
        normalizedRequestedModelIdentifier === normalizedCardId ||
        normalizedRequestedModelIdentifier === normalizedPublicModelId
      );
    });
  }

  private normalizeModelIdentifier(modelIdentifier?: string | null): string {
    if (!modelIdentifier) {
      return '';
    }

    return this.safeDecodeURIComponent(modelIdentifier).trim().toLowerCase();
  }

  private safeDecodeURIComponent(value: string): string {
    try {
      return decodeURIComponent(value);
    } catch {
      return value;
    }
  }

  private updateModelIdQueryParam(modelId: string | null): void {
    this.routeModelId = modelId;
    const queryParams: Params = modelId ? { modelId } : { modelId: null };

    this.router.navigate([], {
      queryParams,
      queryParamsHandling: 'merge',
      replaceUrl: true,
    }).catch((err) => {
      console.error('Error updating modelId query param:', err);
    });
  }

  private applyMoreState(currentCard: ModelCardRendered): void {
    if (!this.collectionDetailsSource?.collectionCards?.length) {
      return;
    }

    this.currentCard = currentCard;
    this.cardService.collectionDetailsInitTimeStart = performance.now();
    this.modelFeedCards = this.collectionDetailsSource.collectionCards;
    this.cardService.setLastCollectionCard(currentCard);
    this.isCollectionDetailsShown = true;
  }

  private triggerShowAuthPopup(): void {
    this.dialog.open(AuthpopupComponent, {
      data: {},
      restoreFocus: false,
      panelClass: 'webpopup-overlay'
    });
  }

  private openAccountPopup(): void {
    const dialogRef = this.dialog.open(WebaccountComponent, {
      restoreFocus: false,
      panelClass: ['webpopup-overlay', 'web-account']
    });

    this.accountPopupSubscription?.unsubscribe();
    this.accountPopupSubscription = dialogRef.componentInstance.webAccountPopupEvent.subscribe((action) => {
      this.handleWebpanelAction(action);
    });

    dialogRef.afterClosed().subscribe(() => {
      this.accountPopupSubscription?.unsubscribe();
      this.accountPopupSubscription = undefined;
    });
  }

  private handleWebpanelAction(action: INavpanelAction): void {
    switch (true) {
      case action.actionType === NAVPANEL_ACTION_TYPE.SHOW_SUBSCRIBE:
        this.openSubscriptionDialog();
        break;
      case action.actionType === NAVPANEL_ACTION_TYPE.STRIPE_HOSTED_CHECKOUT:
        this.stripeHostedCheckoutRequest();
        break;
      case action.actionType === NAVPANEL_ACTION_TYPE.FREE_TRIAL_ACTIVATION:
        this.freeTrialActivation();
        break;
      case action.actionType === NAVPANEL_ACTION_TYPE.SETTINGS:
        this.router.navigateByUrl('/service');
        break;
      default:
        break;
    }
  }

  private openSubscriptionDialog(disabled: boolean = false): void {
    const dialogRef = this.dialog.open(WebsubscriptionComponent, {
      data: disabled,
      restoreFocus: false,
      panelClass: 'compact-webpopup-overlay',
      backdropClass: 'compact-webpopup-backdrop'
    });

    this.subscriptionPopupSubscription?.unsubscribe();
    this.subscriptionPopupSubscription = dialogRef.componentInstance.websubscriptionclicked.subscribe((action) => {
      this.handleWebpanelAction(action);
    });

    dialogRef.afterClosed().subscribe(() => {
      this.subscriptionPopupSubscription?.unsubscribe();
      this.subscriptionPopupSubscription = undefined;
    });
  }

  private stripeHostedCheckoutRequest(): void {
    if (!this.firebaseAuth.currentUser?.uid) {
      this.triggerShowAuthPopup();
      return;
    }

    this.cardService.createCheckOutSession().then((res: any) => {
      this.stripeService.redirectToCheckout({ sessionId: res.sessionId }).subscribe((result) => {
        if (result.error) {
          alert(result.error.message);
        }
      });
    }).catch((error) => {
      this.utilityService.showImportantErrorSnackBar(`Error occured on join membership process: ${JSON.stringify(error.error)}`, 500);
      this.dialog.closeAll();
    });
  }

  private freeTrialActivation(): void {
    this.cardService.activateTrialPostRequest().then(() => {
      if (this.firebaseAuth.currentUser) {
        this.firebaseAuth.currentUser.subscriptionStatus = SUBSCRIPTION_STATUS.TRIAL;
      }

      this.utilityService.showSnackBar('Free Trial Activated', 350);
    }).catch((error) => {
      this.utilityService.showImportantErrorSnackBar(`Error occured on free trial activation: ${JSON.stringify(error.error)}`, 500);
    });
  }

  private openCancelPublishingCardConfirmationDialog(card: ICard): void {
    if (!card) {
      return;
    }

    const dialogRef = this.dialog.open<ConfirmationDialogComponent, DialogData>(ConfirmationDialogComponent, {
      data: this.utilityService.applyPopupText(POPUP_TEXT.CANCEL_PUBLISHING_NO_TITLE, '{modelTitle}', card.cardTitle)
    });

    dialogRef.afterClosed().subscribe(async (result) => {
      if (result !== true) {
        return;
      }

      await this.rejectPublishedModelAndRefresh(card);
    });
  }

  private async rejectPublishedModelAndRefresh(card: ICard): Promise<void> {
    try {
      await this.cardService.rejectReviewModelPutRequest(card);
      this.utilityService.showSnackBar('Model rejected successfully.', 500);
      this.updateModelIdQueryParam(null);
      this.isModelFeedVisible = false;
      await this.reloadPublishedReviewsCollection({ restoreFromQueryParam: false });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : JSON.stringify(error);
      this.utilityService.showErrorSnackBar(`Error occured on rejecting review: ${errorMessage}`, 500);
    }
  }

  ngOnDestroy(): void {
    this.invalidateActiveLoad();
    this.modelIdChangeSubscription?.unsubscribe();
    this.planningSubscription?.unsubscribe();
    this.generationSubscription?.unsubscribe();
    this.accountPopupSubscription?.unsubscribe();
    this.subscriptionPopupSubscription?.unsubscribe();
  }

  private invalidateActiveLoad(): void {
    this.isComponentActive = false;
    this.activeLoadToken += 1;
  }

  private isLoadTokenActive(loadToken: number): boolean {
    return this.isComponentActive && loadToken === this.activeLoadToken;
  }

  private startAdvancedSearch(prompt: string): void {
    this.cancelActiveAdvancedSearchGeneration();
    this.resetAdvancedSearchState('planning');
    this.isAiPreviewFeedActive = false;
    this.isModelFeedVisible = false;
    this.isCollectionDetailsShown = false;
    this.updateModelIdQueryParam(null);

    const generationMode = this.resolveAdvancedSearchGenerationMode();
    const requestToken = ++this.advancedSearchRequestToken;

    this.logAdvancedSearchEvent('Planning started', {
      prompt,
      generationMode,
      requestToken,
    });

    this.planningSubscription?.unsubscribe();
    this.planningSubscription = this.advancedSearchService
      .planAssets(prompt, this.advancedSearchBaselineCount, this.advancedSearchMaxCount)
      .subscribe({
      next: (plannerResponse) => {
        if (!this.isAdvancedSearchTokenActive(requestToken)) {
          return;
        }

        this.currentAdvancedSearchMode = plannerResponse.searchMode;
        this.normalizedAdvancedSearchPrompt = plannerResponse.normalizedPrompt;
        this.advancedSearchCards = this.advancedSearchService.createPlaceholderCards(plannerResponse.assets);
        this.advancedSearchUiState = 'generating';

        this.logAdvancedSearchEvent('Planning completed', {
          requestToken,
          generationMode,
          searchMode: plannerResponse.searchMode,
          recommendedCount: plannerResponse.recommendedCount,
          assetCount: plannerResponse.assets.length,
          assets: plannerResponse.assets.map((asset) => ({
            id: asset.id,
            title: asset.title,
            order: asset.order,
          })),
        });

        this.startPreviewGeneration(plannerResponse, requestToken, generationMode);
      },
      error: (error) => {
        if (!this.isAdvancedSearchTokenActive(requestToken)) {
          return;
        }

        this.advancedSearchUiState = 'error';
        this.advancedSearchErrorMessage = this.handleAdvancedSearchError('planning', error, {
          requestToken,
          prompt,
        });
      }
    });
  }

  private startPreviewGeneration(
    plannerResponse: AdvancedSearchPlannerResponse,
    requestToken: number,
    generationMode: AdvancedSearchGenerationMode,
  ): void {
    this.generationSubscription?.unsubscribe();
    const isSheetMode = generationMode === 'sheet-pro';
    if (isSheetMode) {
      if (!this.isAdvancedSearchSheetAssetCountSupported(plannerResponse.assets.length)) {
        const errorMessage = 'Sheet mode requires exactly 12 or 24 generated assets.';
        this.advancedSearchCards = this.advancedSearchCards.map((card) => ({
          ...card,
          status: 'error',
          errorMessage,
        }));
        this.advancedSearchErrorMessage = errorMessage;
        this.advancedSearchUiState = 'error';
        return;
      }

      const sheetBatches = this.partitionAssetsForSheetGeneration(plannerResponse.assets);
      const totalBatches = sheetBatches.length;
      this.generationSubscription = from(sheetBatches).pipe(
        concatMap(
          (assetsBatch, batchIndex) => this.generateSheetBatch$(
            assetsBatch,
            plannerResponse,
            generationMode,
            requestToken,
            batchIndex + 1,
            totalBatches,
          ),
        ),
        finalize(() => this.finalizeAdvancedSearchGeneration(requestToken)),
      ).subscribe();
      return;
    }

    this.generationSubscription = this.generateSingleAssetBatch$(plannerResponse.assets, plannerResponse, requestToken).pipe(
      finalize(() => this.finalizeAdvancedSearchGeneration(requestToken)),
    ).subscribe();
  }

  private resolveAdvancedSearchGenerationMode(): AdvancedSearchGenerationMode {
    return this.routeAdvancedSearchGenerationMode || this.settingsService.advancedSearchGenerationMode;
  }

  private partitionAssetsForSheetGeneration(assets: AdvancedSearchPlannerAsset[]): AdvancedSearchPlannerAsset[][] {
    const sheetBatches: AdvancedSearchPlannerAsset[][] = [];

    for (let index = 0; index < assets.length; index += this.advancedSearchSheetBatchSize) {
      sheetBatches.push(assets.slice(index, index + this.advancedSearchSheetBatchSize));
    }

    return sheetBatches;
  }

  private isAdvancedSearchSheetAssetCountSupported(assetCount: number): boolean {
    return assetCount >= this.advancedSearchSheetBatchSize && assetCount % this.advancedSearchSheetBatchSize === 0;
  }

  private generateSheetBatch$(
    assetsBatch: AdvancedSearchPlannerAsset[],
    plannerResponse: AdvancedSearchPlannerResponse,
    generationMode: AdvancedSearchGenerationMode,
    requestToken: number,
    batchNumber: number,
    totalBatches: number,
  ): Observable<GenerateAdvancedSearchAssetSheetResponse | null> {
    assetsBatch.forEach((asset) => {
      this.updateAdvancedSearchCard(asset.id, {
        status: 'loading',
        errorMessage: undefined,
      });
    });

    this.logAdvancedSearchEvent('Sheet batch started', {
      requestToken,
      batchNumber,
      totalBatches,
      generationMode,
      searchMode: plannerResponse.searchMode,
      normalizedPrompt: plannerResponse.normalizedPrompt,
      assets: assetsBatch.map((asset) => ({
        id: asset.id,
        title: asset.title,
        order: asset.order,
      })),
    });

    return timer(batchNumber === 1 ? 0 : this.advancedSearchSheetBatchDelayMs).pipe(
      concatMap(() => {
        const requestPayload = {
          normalizedPrompt: plannerResponse.normalizedPrompt,
          searchMode: plannerResponse.searchMode,
          assets: assetsBatch.map((asset) => ({
            assetId: asset.id,
            title: asset.title,
            imagePrompt: asset.imagePrompt,
          })),
        };

        return this.advancedSearchService.generateAssetSheetPro(requestPayload);
      }),
      retry({
        count: this.advancedSearchSheetBatchRetryCount,
        delay: (error, retryAttempt) => {
          if (!this.isRetriableAdvancedSearchSheetError(error)) {
            throw error;
          }

          const retryDelayMs = this.advancedSearchSheetBatchRetryDelayMs * retryAttempt;
          this.logAdvancedSearchEvent('Sheet batch retry scheduled', {
            requestToken,
            batchNumber,
            totalBatches,
            retryAttempt,
            retryDelayMs,
            status: error instanceof HttpErrorResponse ? error.status : undefined,
          });

          return timer(retryDelayMs);
        },
      }),
      tap((response) => {
        if (!this.isAdvancedSearchTokenActive(requestToken)) {
          return;
        }

        this.logAdvancedSearchEvent('Sheet batch completed', {
          requestToken,
          batchNumber,
          totalBatches,
          generationMode,
          resultCount: response.results.length,
          fallbackCount: response.fallbackCount,
          assets: assetsBatch.map((asset) => ({
            id: asset.id,
            title: asset.title,
          })),
        });

        this.applyGeneratedPreviews(response.results);
      }),
      catchError((error) => {
        if (!this.isAdvancedSearchTokenActive(requestToken)) {
          return of(null);
        }

        const errorMessage = this.handleAdvancedSearchError('sheet-batch', error, {
          assets: assetsBatch,
          batchNumber,
          totalBatches,
          requestToken,
          prompt: plannerResponse.normalizedPrompt,
        });

        assetsBatch.forEach((asset) => {
          this.updateAdvancedSearchCard(asset.id, {
            status: 'error',
            errorMessage,
          });
        });

        if (!this.advancedSearchErrorMessage) {
          this.advancedSearchErrorMessage = errorMessage;
        }

        return of(null);
      }),
    );
  }

  private generateSingleAssetBatch$(
    assets: AdvancedSearchPlannerAsset[],
    plannerResponse: AdvancedSearchPlannerResponse,
    requestToken: number,
  ): Observable<GenerateAdvancedSearchAssetResponse | null> {
    const generationMode = this.resolveAdvancedSearchGenerationMode();

    return from(assets).pipe(
      mergeMap(
        (asset) => this.generateSingleAssetPreview$(asset, plannerResponse, requestToken, generationMode),
        this.advancedSearchGenerationConcurrency,
      ),
    );
  }

  private generateSingleAssetPreview$(
    asset: AdvancedSearchPlannerAsset,
    plannerResponse: AdvancedSearchPlannerResponse,
    requestToken: number,
    generationMode: AdvancedSearchGenerationMode = this.resolveAdvancedSearchGenerationMode(),
  ): Observable<GenerateAdvancedSearchAssetResponse | null> {
    this.updateAdvancedSearchCard(asset.id, {
      status: 'loading',
      errorMessage: undefined,
    });

    return this.advancedSearchService.generateAssetPreview({
      assetId: asset.id,
      title: asset.title,
      imagePrompt: asset.imagePrompt,
      normalizedPrompt: plannerResponse.normalizedPrompt,
      searchMode: plannerResponse.searchMode,
      generationMode:
        'single-pro',
    }).pipe(
      tap((response) => {
        if (!this.isAdvancedSearchTokenActive(requestToken)) {
          return;
        }

        this.applyGeneratedPreview(response);
      }),
      catchError((error) => {
        if (this.isAdvancedSearchTokenActive(requestToken)) {
          const errorMessage = this.handleAdvancedSearchError('single-asset', error, {
            assets: [asset],
            requestToken,
            prompt: plannerResponse.normalizedPrompt,
            notifyUser: !this.advancedSearchErrorMessage,
          });

          this.updateAdvancedSearchCard(asset.id, {
            status: 'error',
            errorMessage,
          });

          if (!this.advancedSearchErrorMessage) {
            this.advancedSearchErrorMessage = errorMessage;
          }
        }

        return of(null);
      }),
    );
  }

  private resetAdvancedSearchState(nextState: AdvancedSearchUiState = 'idle'): void {
    this.advancedSearchUiState = nextState;
    this.advancedSearchCards = [];
    this.advancedSearchErrorMessage = '';
    this.normalizedAdvancedSearchPrompt = '';
    this.currentAdvancedSearchMode = 'scene-kit';
  }

  private cancelActiveAdvancedSearchGeneration(): void {
    this.planningSubscription?.unsubscribe();
    this.planningSubscription = undefined;
    this.generationSubscription?.unsubscribe();
    this.generationSubscription = undefined;
    this.advancedSearchRequestToken += 1;
  }

  private isAdvancedSearchTokenActive(requestToken: number): boolean {
    return requestToken === this.advancedSearchRequestToken;
  }

  private finalizeAdvancedSearchGeneration(requestToken: number): void {
    if (!this.isAdvancedSearchTokenActive(requestToken)) {
      return;
    }

    const hasSuccessfulCards = this.advancedSearchCards.some((card) => card.status === 'success');
    const hasErroredCards = this.advancedSearchCards.some((card) => card.status === 'error');

    if (!hasSuccessfulCards && hasErroredCards) {
      this.advancedSearchUiState = 'error';
      if (!this.advancedSearchErrorMessage) {
        this.advancedSearchErrorMessage = 'Unable to generate sheet previews. Please try again.';
      }
      return;
    }

    this.advancedSearchUiState = 'success';
  }

  private updateAdvancedSearchCard(cardId: string, patch: Partial<AdvancedSearchPreviewCard>): void {
    this.advancedSearchCards = this.advancedSearchCards.map((card) => {
      if (card.id !== cardId) {
        return card;
      }

      return {
        ...card,
        ...patch,
      };
    });
  }

  private applyGeneratedPreview(response: GenerateAdvancedSearchAssetResponse): void {
    this.updateAdvancedSearchCard(response.assetId, {
      status: 'success',
      imageDataUrl: response.imageDataUrl,
      thumbnailImageDataUrl: response.thumbnailImageDataUrl,
      viewerImageDataUrl: response.viewerImageDataUrl,
      promptUsed: response.promptUsed,
      viewerPromptUsed: response.viewerPromptUsed,
      source: response.source,
      errorMessage: undefined,
    });
  }

  private applyGeneratedPreviews(responses: GenerateAdvancedSearchAssetResponse[]): void {
    responses.forEach((response) => this.applyGeneratedPreview(response));
  }

  private createAiViewerCard(card: AdvancedSearchPreviewCard, index: number): ModelCardRendered {
    const fallbackId = `ai-preview-${index + 1}`;
    const viewerImageDataUrl = card.viewerImageDataUrl || card.imageDataUrl || '';

    return {
      id: card.id || fallbackId,
      cardSharingId: card.id || fallbackId,
      publicModelId: card.id || fallbackId,
      cardContentMobile: viewerImageDataUrl,
      cardPosterMobile: viewerImageDataUrl,
      cardContentWeb: viewerImageDataUrl,
      cardPosterWeb: viewerImageDataUrl,
      cardModelLink: '',
      cardType: CARD_TYPE.MODEL,
      cardCategory: ADVANCED_SEARCH_MODE_LABELS[this.currentAdvancedSearchMode],
      cardAuthor: 'AI Preview',
      cardTitle: card.title,
      cardDescription: card.viewerPromptUsed || card.promptUsed || card.imagePrompt,
      cardPrice: 0,
      purchaseStatus: CARD_PURCHASE_STATUS.DRAFT_NOT_LIKED,
      modifiedDate: new Date().toISOString(),
      visible: false,
      isFullscreen: false,
      cardShortView: false,
      isVideoShown: false,
      isPosterShown: true,
      canBePlayed: true,
    };
  }

  private logAdvancedSearchEvent(message: string, context: Record<string, unknown>): void {
    try {
      this.utilityService.rollbarInfo(`[AdvancedSearch] ${message}: ${JSON.stringify(context)}`);
    } catch (error) {
      this.utilityService.rollbarInfo(`[AdvancedSearch] ${message}`);
    }
  }

  private isRetriableAdvancedSearchSheetError(error: unknown): boolean {
    if (!(error instanceof HttpErrorResponse)) {
      return false;
    }

    return error.status === 0 || [408, 409, 425, 429, 500, 502, 503, 504].includes(error.status);
  }

  private handleAdvancedSearchError(
    phase: 'planning' | 'sheet-batch' | 'single-asset' | 'retry',
    error: unknown,
    options: {
      assets?: Pick<AdvancedSearchPlannerAsset, 'id' | 'title'>[];
      batchNumber?: number;
      totalBatches?: number;
      requestToken?: number;
      prompt?: string;
      notifyUser?: boolean;
    } = {},
  ): string {
    const errorTitle = this.getAdvancedSearchErrorTitle(phase);
    const assets = options.assets || [];
    const assetSummary = this.summarizeAdvancedSearchAssets(assets);
    const displayMessage = [errorTitle, assetSummary ? `Assets: ${assetSummary}` : undefined]
      .filter(Boolean)
      .join(' | ');
    const contextData = {
      phase,
      batchNumber: options.batchNumber,
      totalBatches: options.totalBatches,
      requestToken: options.requestToken,
      generationMode: this.resolveAdvancedSearchGenerationMode(),
      searchMode: this.currentAdvancedSearchMode,
      uiState: this.advancedSearchUiState,
      normalizedPrompt: options.prompt || this.normalizedAdvancedSearchPrompt || undefined,
      assets: assets.map((asset) => ({
        id: asset.id,
        title: asset.title,
      })),
    };

    console.error(`[AdvancedSearch] ${errorTitle}`, {
      ...contextData,
      error,
    });

    if (options.notifyUser === false) {
      return [displayMessage, this.utilityService.formatErrorForDisplay(error)]
        .filter(Boolean)
        .join(' | ');
    }

    return this.utilityService.importantError(errorTitle, error, {
      duration: phase === 'sheet-batch' ? 15000 : 12000,
      toastContext: assetSummary ? `Assets: ${assetSummary}` : undefined,
      contextData,
    });
  }

  private getAdvancedSearchErrorTitle(
    phase: 'planning' | 'sheet-batch' | 'single-asset' | 'retry',
  ): string {
    switch (phase) {
      case 'planning':
        return 'AI preview planning failed';
      case 'sheet-batch':
        return `${this.getCurrentSheetGenerationModeLabel()} render batch failed`;
      case 'single-asset':
        return 'Preview render failed';
      case 'retry':
        return 'Preview retry failed';
      default:
        return 'AI preview request failed';
    }
  }

  private summarizeAdvancedSearchAssets(assets: Pick<AdvancedSearchPlannerAsset, 'id' | 'title'>[]): string {
    const assetLabels = assets
      .map((asset) => (asset.title || asset.id || '').trim())
      .filter(Boolean);

    if (assetLabels.length === 0) {
      return '';
    }

    const preview = assetLabels.slice(0, 3).join(', ');
    return assetLabels.length > 3
      ? `${preview} +${assetLabels.length - 3} more`
      : preview;
  }

  private getSheetGenerationModeLabel(mode: AdvancedSearchGenerationMode): string {
    return getAdvancedSearchGenerationModeLabel(mode);
  }

  private getCurrentSheetGenerationModeLabel(): string {
    return this.getSheetGenerationModeLabel(this.resolveAdvancedSearchGenerationMode());
  }
}
